import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { toggleAttended, fetchBatchEventStatus, getIdToken } from '../attendedUtils';

const TABS = ['Show Archive', 'Shows by Year', 'Artists Hosted', 'Promoters', 'Stages', 'Flyers'];
const FLYERS_SUBTABS = ['Flyer History', 'Shows Without Flyers'];
const PAGE_SIZE = 25;
const FLYER_PAGE_SIZE = 50;
const FLYER_COLS = 5;

const FLYER_SORT_OPTIONS = [
    { value: 'date_desc', label: 'Date (Most Recent to Oldest)' },
    { value: 'date_asc',  label: 'Date (Oldest to Most Recent)' },
    { value: 'att_desc',  label: 'Attendance (Most to Least)' },
    { value: 'att_asc',   label: 'Attendance (Least to Most)' },
];

function Pagination({ currentPage, totalItems, onPageChange }) {
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) pages.push(i);

    return (
        <div style={styles.pagination}>
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}
            >
                ‹ Prev
            </button>
            {pages.map(p => (
                <button
                    key={p}
                    onClick={() => onPageChange(p)}
                    style={{ ...styles.pageBtn, ...(p === currentPage ? styles.pageBtnActive : {}) }}
                >
                    {p}
                </button>
            ))}
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{ ...styles.pageBtn, ...(currentPage === totalPages ? styles.pageBtnDisabled : {}) }}
            >
                Next ›
            </button>
        </div>
    );
}

function FlyerPagination({ currentPage, totalItems, onPageChange }) {
    const totalPages = Math.ceil(totalItems / FLYER_PAGE_SIZE);
    if (totalPages <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return (
        <div style={styles.pagination}>
            <button onClick={() => { onPageChange(currentPage - 1); window.scrollTo(0, 0); }} disabled={currentPage === 1} style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}>‹ Prev</button>
            {pages.map(p => (
                <button key={p} onClick={() => { onPageChange(p); window.scrollTo(0, 0); }} style={{ ...styles.pageBtn, ...(p === currentPage ? styles.pageBtnActive : {}) }}>{p}</button>
            ))}
            <button onClick={() => { onPageChange(currentPage + 1); window.scrollTo(0, 0); }} disabled={currentPage === totalPages} style={{ ...styles.pageBtn, ...(currentPage === totalPages ? styles.pageBtnDisabled : {}) }}>Next ›</button>
        </div>
    );
}

function SubTabBar({ tabs, active, onChange }) {
    return (
        <div style={styles.subTabBar}>
            {tabs.map((tab, i) => (
                <button
                    key={tab}
                    onClick={() => onChange(i)}
                    style={{ ...styles.subTabBtn, ...(active === i ? styles.subTabBtnActive : {}) }}
                >
                    {tab}
                </button>
            ))}
        </div>
    );
}

function ShowHistoryTab({ shows, attendedMap, performedMap, workedMap, onToggleAttended, togglingMap }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded at this venue yet.</div>;

    const q = search.toLowerCase();
    const filtered = search ? shows.filter(show => {
        const dateStr = show.event_date
            ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
            : '';
        return [dateStr, show.headliners, show.support_acts, show.stage_name, show.festival_name]
            .some(val => val && val.toLowerCase().includes(q));
    }) : shows;

    const totalShows = shows.length;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

    return (
        <div>
            <input
                type="text"
                placeholder="Search by date, artists, or stage..."
                value={search}
                onChange={handleSearch}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No shows match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Show #</th>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Artists</th>
                                    <th style={styles.th}>Stage</th>
                                    <th style={styles.th}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((show, idx) => {
                                    const globalIdx = shows.indexOf(show);
                                    const showNum = totalShows - globalIdx;
                                    const dateObj = show.event_date ? new Date(show.event_date) : null;
                                    const formattedDate = dateObj
                                        ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                        : '—';
                                    const isAttended = attendedMap[show.event_id] === true;
                                    const isPerformed = performedMap[show.event_id] === true;
                                    const isWorked = workedMap[show.event_id] === true;
                                    const isToggling = togglingMap[show.event_id];

                                    return (
                                        <tr key={show.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                <Link to={`/events/${show.event_slug || show.event_id}`} style={styles.tableLink}>{showNum}</Link>
                                            </td>
                                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{formattedDate}</td>
                                            <td style={{ ...styles.td, color: '#ccc', fontSize: '13px' }}>
                                                {(() => {
                                                    const allArtists = [show.headliners, show.support_acts].filter(Boolean).join(', ');
                                                    const artistStr = show.festival_name && show.festival_slug
                                                        ? null
                                                        : allArtists || null;
                                                    const titlePrefix = show.event_title ? `${show.event_title}: ` : '';
                                                    if (show.festival_name && show.festival_slug) {
                                                        return <>{titlePrefix}<Link to={`/festivals/${show.festival_slug}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' }}>{show.festival_name}</Link>{allArtists ? `, ${allArtists}` : ''}</>;
                                                    }
                                                    const full = [titlePrefix + (artistStr || '')].join('').trim();
                                                    return full || '—';
                                                })()}
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                {show.stage_name || '—'}
                                            </td>
                                            <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {isPerformed ? (
                                                    <span style={styles.performedBadge}>Performed</span>
                                                ) : isWorked ? (
                                                    <span style={styles.workedBadge}>Worked</span>
                                                ) : (
                                                    <button
                                                        onClick={() => onToggleAttended(show.event_id)}
                                                        disabled={isToggling}
                                                        style={isAttended ? styles.attendedBtnOn : styles.attendedBtnOff}
                                                    >
                                                        {isToggling ? '…' : isAttended ? '✓ Attended' : 'Attended?'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

function ShowsByYearTab({ shows }) {
    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded at this venue yet.</div>;

    const years = shows.filter(s => s.event_date).map(s => new Date(s.event_date).getUTCFullYear());
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    const counts = {};
    years.forEach(y => { counts[y] = (counts[y] || 0) + 1; });
    const data = [];
    for (let y = minYear; y <= maxYear; y++) data.push({ year: y, count: counts[y] || 0 });

    const maxCount = Math.max(...data.map(d => d.count), 1);
    const barMaxHeight = 200;
    const step = maxCount <= 5 ? 1 : maxCount <= 10 ? 2 : maxCount <= 20 ? 5 : 10;
    const gridLines = [];
    for (let v = 0; v <= maxCount; v += step) gridLines.push(v);
    if (gridLines[gridLines.length - 1] < maxCount) gridLines.push(maxCount);
    const chartWidth = Math.max(data.length * 52, 400);
    const yAxisWidth = 36;
    const chartPadTop = 16;
    const chartPadBottom = 40;
    const totalHeight = barMaxHeight + chartPadTop + chartPadBottom;

    return (
        <div>
            <div style={{ overflowX: 'auto', paddingBottom: '8px' }}>
                <div style={{ display: 'flex', minWidth: chartWidth + yAxisWidth + 'px' }}>
                    <div style={{ width: yAxisWidth + 'px', flexShrink: 0, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => {
                            const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight;
                            return <div key={v} style={{ position: 'absolute', top: top + 'px', right: '6px', transform: 'translateY(-50%)', fontSize: '11px', color: '#666', whiteSpace: 'nowrap' }}>{v}</div>;
                        })}
                    </div>
                    <div style={{ flex: 1, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => {
                            const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight;
                            return <div key={v} style={{ position: 'absolute', top: top + 'px', left: 0, right: 0, borderTop: `1px solid ${v === 0 ? '#444' : '#2a2a2a'}`, zIndex: 0 }} />;
                        })}
                        <div style={{ display: 'flex', alignItems: 'flex-end', height: barMaxHeight + 'px', marginTop: chartPadTop + 'px', gap: '4px', paddingLeft: '8px', paddingRight: '8px', position: 'relative', zIndex: 1 }}>
                            {data.map(({ year, count }) => {
                                const barH = count === 0 ? 0 : Math.max((count / maxCount) * barMaxHeight, 4);
                                return (
                                    <div key={year} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }} title={`${year}: ${count} show${count !== 1 ? 's' : ''}`}>
                                        {count > 0 && <div style={{ fontSize: '11px', color: '#bb86fc', fontWeight: 'bold', opacity: 0.85 }}>{count}</div>}
                                        <div style={{ width: '100%', maxWidth: '44px', height: barH + 'px', backgroundColor: count === 0 ? 'transparent' : '#bb86fc', borderRadius: '3px 3px 0 0' }} />
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', paddingLeft: '8px', paddingRight: '8px', gap: '4px', marginTop: '6px' }}>
                            {data.map(({ year, count }) => (
                                <div key={year} style={{ flex: 1, textAlign: 'center', fontSize: '11px', color: count === 0 ? '#444' : '#888', whiteSpace: 'nowrap', overflow: 'hidden' }}>{year}</div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '32px', fontSize: '13px', color: '#888' }}>
                <span>Total Years Active: <strong style={{ color: '#e0e0e0' }}>{data.filter(d => d.count > 0).length}</strong></span>
                <span>Peak Year: <strong style={{ color: '#bb86fc' }}>{data.reduce((best, d) => d.count > best.count ? d : best, data[0]).year} ({data.reduce((best, d) => d.count > best.count ? d : best, data[0]).count} shows)</strong></span>
            </div>
        </div>
    );
}

function ArtistsHostedTab({ artists }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!artists || artists.length === 0) return <div style={styles.emptyTab}>No artist data recorded yet.</div>;

    const q = search.toLowerCase();
    const filtered = search ? artists.filter(a =>
        [a.name, a.artist_type, a.artist_style, a.city, a.state, a.country]
            .some(val => val && val.toLowerCase().includes(q))
    ) : artists;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

    return (
        <div>
            <input
                type="text"
                placeholder="Search by artist, type, style, city, state, country..."
                value={search}
                onChange={handleSearch}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No artists match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Artist</th>
                                    <th style={styles.th}>Artist Type</th>
                                    <th style={styles.th}>Artist Style</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State/Region</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Appearances</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((artist, idx) => (
                                    <tr key={artist.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}>
                                            <Link to={`/artists/${artist.slug || artist.id}`} style={styles.tableLink}>{artist.name}</Link>
                                        </td>
                                        <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{artist.artist_type || '—'}</td>
                                        <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{artist.artist_style || '—'}</td>
                                        <td style={styles.td}>{artist.city || '—'}</td>
                                        <td style={styles.td}>{artist.state || '—'}</td>
                                        <td style={styles.td}>{artist.country || '—'}</td>
                                        <td style={styles.td}>
                                            <span style={styles.showCount}>{artist.appearances}x</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Promoters Hosted Tab ──────────────────────────────────────────────────────
function PromotersHostedTab({ promoters }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!promoters || promoters.length === 0) return <div style={styles.emptyTab}>No promoter data recorded yet.</div>;

    const q = search.toLowerCase();
    const filtered = search ? promoters.filter(p =>
        [p.name, p.city, p.state, p.country]
            .some(val => val && val.toLowerCase().includes(q))
    ) : promoters;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input
                type="text"
                placeholder="Search by promoter, city, state, country..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No promoters match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Promoter</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State/Region</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}># of Shows</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((p, idx) => (
                                    <tr key={p.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}>
                                            <Link to={`/promoters/${p.slug || p.id}`} style={styles.tableLink}>{p.name}</Link>
                                        </td>
                                        <td style={styles.td}>{p.city || '—'}</td>
                                        <td style={styles.td}>{p.state || '—'}</td>
                                        <td style={styles.td}>{p.country || '—'}</td>
                                        <td style={styles.td}>
                                            <span style={styles.showCount}>{p.show_count}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

function StagesTab({ stages, venueId, onStagesUpdated }) {
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const emptyForm = { name: '', capacity: '', description: '', is_default: false };
    const [form, setForm] = useState(emptyForm);

    if (!stages) return <div style={styles.emptyTab}>Loading stages...</div>;

    const handleAdd = () => {
        setForm(emptyForm);
        setEditingId(null);
        setShowAddForm(true);
        setDeleteError('');
    };

    const handleEdit = (stage) => {
        setForm({
            name: stage.name || '',
            capacity: stage.capacity != null ? String(stage.capacity) : '',
            description: stage.description || '',
            is_default: stage.is_default || false,
        });
        setEditingId(stage.id);
        setShowAddForm(false);
        setDeleteError('');
    };

    const handleCancel = () => {
        setShowAddForm(false);
        setEditingId(null);
        setForm(emptyForm);
        setDeleteError('');
    };

    const handleSaveNew = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            await axios.post(`${config.API_URL}/venues/${venueId}/stages`, {
                name: form.name.trim(),
                capacity: form.capacity ? parseInt(form.capacity) : null,
                description: form.description.trim() || null,
                is_default: form.is_default,
            });
            setShowAddForm(false);
            setForm(emptyForm);
            onStagesUpdated();
        } catch (err) {
            console.error('Failed to create stage', err);
        }
        setSaving(false);
    };

    const handleSaveEdit = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            await axios.put(`${config.API_URL}/stages/${editingId}`, {
                name: form.name.trim(),
                capacity: form.capacity ? parseInt(form.capacity) : null,
                description: form.description.trim() || null,
                is_default: form.is_default,
            });
            setEditingId(null);
            setForm(emptyForm);
            onStagesUpdated();
        } catch (err) {
            console.error('Failed to update stage', err);
        }
        setSaving(false);
    };

    const handleDelete = async (stage) => {
        if (stage.is_default) {
            setDeleteError(`"${stage.name}" is the default stage and cannot be deleted. Set another stage as default first.`);
            return;
        }
        if (!window.confirm(`Delete stage "${stage.name}"? Any events linked to this stage will be unlinked.`)) return;
        setDeleteError('');
        try {
            await axios.delete(`${config.API_URL}/stages/${stage.id}`);
            onStagesUpdated();
        } catch (err) {
            setDeleteError('Failed to delete stage.');
        }
    };

    return (
        <div>
            {deleteError && (
                <div style={styles.stageError}>{deleteError}</div>
            )}

            {stages.length === 0 ? (
                <div style={styles.emptyTab}>No stages recorded yet.</div>
            ) : (
                <div style={styles.stageList}>
                    {stages.map(stage => (
                        <div key={stage.id} style={styles.stageCard}>
                            {editingId === stage.id ? (
                                <StageForm
                                    form={form}
                                    setForm={setForm}
                                    onSave={handleSaveEdit}
                                    onCancel={handleCancel}
                                    saving={saving}
                                />
                            ) : (
                                <div style={styles.stageCardInner}>
                                    <div style={styles.stageCardLeft}>
                                        <div style={styles.stageName}>
                                            {stage.name}
                                            {stage.is_default && (
                                                <span style={styles.stageBadge}>Default</span>
                                            )}
                                        </div>
                                        {stage.capacity && (
                                            <div style={styles.stageMeta}>Capacity: {stage.capacity.toLocaleString()}</div>
                                        )}
                                        {stage.description && (
                                            <div style={styles.stageDesc}>{stage.description}</div>
                                        )}
                                    </div>
                                    <div style={styles.stageCardActions}>
                                        <button onClick={() => handleEdit(stage)} style={styles.stageEditBtn}>Edit</button>
                                        <button onClick={() => handleDelete(stage)} style={styles.stageDeleteBtn}>Delete</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {showAddForm && (
                <div style={{ ...styles.stageCard, marginTop: '16px' }}>
                    <div style={styles.stageFormTitle}>New Stage</div>
                    <StageForm
                        form={form}
                        setForm={setForm}
                        onSave={handleSaveNew}
                        onCancel={handleCancel}
                        saving={saving}
                    />
                </div>
            )}

            {!showAddForm && (
                <button onClick={handleAdd} style={styles.addStageBtn}>+ Add Stage</button>
            )}
        </div>
    );
}

function StageForm({ form, setForm, onSave, onCancel, saving }) {
    return (
        <div>
            <div style={styles.stageFormRow}>
                <label style={styles.stageFormLabel}>Stage Name *</label>
                <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Main Stage, Tent Stage"
                    style={styles.stageInput}
                />
            </div>
            <div style={styles.stageFormRow}>
                <label style={styles.stageFormLabel}>Capacity</label>
                <input
                    type="number"
                    value={form.capacity}
                    onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))}
                    placeholder="Stage capacity"
                    style={{ ...styles.stageInput, width: '180px' }}
                />
            </div>
            <div style={styles.stageFormRow}>
                <label style={styles.stageFormLabel}>Description</label>
                <textarea
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Optional notes about this stage"
                    rows={2}
                    style={{ ...styles.stageInput, resize: 'vertical' }}
                />
            </div>
            <div style={{ ...styles.stageFormRow, alignItems: 'center', gap: '10px', flexDirection: 'row' }}>
                <input
                    type="checkbox"
                    id="is_default_stage"
                    checked={form.is_default}
                    onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))}
                    style={{ accentColor: '#bb86fc', width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <label htmlFor="is_default_stage" style={{ ...styles.stageFormLabel, margin: 0, cursor: 'pointer' }}>
                    Set as default stage
                </label>
            </div>
            <div style={styles.stageFormActions}>
                <button onClick={onSave} disabled={saving || !form.name.trim()} style={styles.stageSaveBtn}>
                    {saving ? 'Saving...' : 'Save'}
                </button>
                <button onClick={onCancel} style={styles.stageCancelBtn}>Cancel</button>
            </div>
        </div>
    );
}

// ── Flyer Card ────────────────────────────────────────────────────────────────
function FlyerCard({ show }) {
    const [hovered, setHovered] = useState(false);
    const [imgError, setImgError] = useState(false);
    const formattedDate = show.event_date
        ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
        : '';
    const tooltip = [formattedDate, show.headliners, show.stage_name].filter(Boolean).join(' · ');
    return (
        <Link
            to={`/events/${show.event_slug || show.event_id}`}
            style={styles.flyerCell}
            title={tooltip}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {imgError ? (
                <div style={styles.flyerErrorBox}>
                    <div style={styles.flyerErrorIcon}>🎟️</div>
                    <div style={styles.flyerErrorDate}>{formattedDate}</div>
                    <div style={styles.flyerErrorVenue}>{show.headliners || ''}</div>
                </div>
            ) : (
                <img
                    src={show.flyer_url}
                    alt={tooltip}
                    style={{ ...styles.flyerImg, opacity: hovered ? 0.85 : 1 }}
                    loading="lazy"
                    onError={() => setImgError(true)}
                />
            )}
            {!imgError && (
                <div style={{ ...styles.flyerOverlay, opacity: hovered ? 1 : 0 }}>
                    <div style={styles.flyerDate}>{formattedDate}</div>
                </div>
            )}
        </Link>
    );
}

// ── Flyer History Tab ─────────────────────────────────────────────────────────
function FlyerHistoryTab({ shows }) {
    const [page, setPage] = useState(1);
    const [sortKey, setSortKey] = useState('date_desc');
    const [filterType, setFilterType] = useState('artist');
    const [filterSearch, setFilterSearch] = useState('');

    const flyers = shows ? shows.filter(s => s.flyer_url) : [];

    if (flyers.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>🎟️</div>
                <div style={{ color: '#888', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No Flyers Yet</div>
                <div style={{ color: '#555', fontSize: '14px' }}>Event flyers will appear here as they are added.</div>
            </div>
        );
    }

    // Apply filter
    const q = filterSearch.trim().toLowerCase();
    const filtered = q ? flyers.filter(s => {
        if (filterType === 'artist') return (s.headliners && s.headliners.toLowerCase().includes(q)) || (s.support_acts && s.support_acts.toLowerCase().includes(q));
        if (filterType === 'festival') return s.festival_name && s.festival_name.toLowerCase().includes(q);
        if (filterType === 'promoter') return s.promoters && s.promoters.toLowerCase().includes(q);
        return true;
    }) : flyers;

    // Apply sort
    const sorted = [...filtered].sort((a, b) => {
        if (sortKey === 'date_desc') {
            return new Date(b.event_date) - new Date(a.event_date);
        } else if (sortKey === 'date_asc') {
            return new Date(a.event_date) - new Date(b.event_date);
        } else if (sortKey === 'att_desc') {
            const aVal = a.estimated_attendance != null ? parseInt(a.estimated_attendance) : -1;
            const bVal = b.estimated_attendance != null ? parseInt(b.estimated_attendance) : -1;
            return bVal - aVal;
        } else if (sortKey === 'att_asc') {
            const aVal = a.estimated_attendance != null ? parseInt(a.estimated_attendance) : Infinity;
            const bVal = b.estimated_attendance != null ? parseInt(b.estimated_attendance) : Infinity;
            return aVal - bVal;
        }
        return 0;
    });

    const totalPages = Math.ceil(sorted.length / FLYER_PAGE_SIZE);
    const paginated = sorted.slice((page - 1) * FLYER_PAGE_SIZE, page * FLYER_PAGE_SIZE);
    const remainder = paginated.length % FLYER_COLS;
    const padCount = remainder === 0 ? 0 : FLYER_COLS - remainder;

    const handleSortChange = (e) => { setSortKey(e.target.value); setPage(1); };
    const handleFilterTypeChange = (e) => { setFilterType(e.target.value); setFilterSearch(''); setPage(1); };
    const handleFilterSearchChange = (e) => { setFilterSearch(e.target.value); setPage(1); };
    const filterPlaceholder = filterType === 'artist' ? 'Search artist...' : filterType === 'festival' ? 'Search festival...' : 'Search promoter...';

    return (
        <div>
            {/* Controls bar: filter on left, sort on right */}
            <div style={styles.flyerControlsBar}>
                {/* Filter */}
                <div style={styles.flyerFilterGroup}>
                    <select value={filterType} onChange={handleFilterTypeChange} style={styles.flyerFilterTypeSelect}>
                        <option value="artist">Artist</option>
                        <option value="festival">Festival</option>
                        <option value="promoter">Promoter</option>
                    </select>
                    <input
                        type="text"
                        value={filterSearch}
                        onChange={handleFilterSearchChange}
                        placeholder={filterPlaceholder}
                        style={styles.flyerFilterInput}
                    />
                    {filterSearch && (
                        <button onClick={() => { setFilterSearch(''); setPage(1); }} style={styles.flyerFilterClear} title="Clear filter">✕</button>
                    )}
                </div>
                {/* Sort */}
                <div style={styles.flyerSortGroup}>
                    <label style={styles.flyerSortLabel}>Sort by</label>
                    <select value={sortKey} onChange={handleSortChange} style={styles.flyerSortSelect}>
                        {FLYER_SORT_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {sorted.length === 0 ? (
                <div style={styles.emptyTab}>No flyers match your filter.</div>
            ) : (
                <>
                    <div style={styles.flyerGrid}>
                        {paginated.map((show) => (
                            <FlyerCard key={show.event_id} show={show} />
                        ))}
                        {Array.from({ length: padCount }).map((_, i) => (
                            <div key={`pad-${i}`} style={styles.flyerCellPad} />
                        ))}
                    </div>
                    {totalPages > 1 && (
                        <FlyerPagination currentPage={page} totalItems={sorted.length} onPageChange={setPage} />
                    )}
                    <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: '#555' }}>
                        {q ? `${sorted.length} of ${flyers.length} flyer${flyers.length !== 1 ? 's' : ''}` : `${flyers.length} flyer${flyers.length !== 1 ? 's' : ''}`}
                        {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ''}
                    </div>
                </>
            )}
        </div>
    );
}

// ── Shows Without Flyers Tab ──────────────────────────────────────────────────
function ShowsWithoutFlyersTab({ shows }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    const noFlyer = shows ? shows.filter(s => !s.flyer_url) : [];

    if (noFlyer.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>🎉</div>
                <div style={{ color: '#888', fontSize: '15px' }}>All shows have a flyer — nothing missing here.</div>
            </div>
        );
    }

    const q = search.toLowerCase();
    const filtered = search ? noFlyer.filter(s => {
        const dateStr = s.event_date ? new Date(s.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '';
        return [dateStr, s.headliners, s.support_acts, s.stage_name, s.festival_name].some(v => v && v.toLowerCase().includes(q));
    }) : noFlyer;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input
                type="text"
                placeholder="Search by date, artists, stage, festival..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No shows match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Venue / Stage</th>
                                    <th style={styles.th}>Lineup</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((s, idx) => {
                                    const formattedDate = s.event_date
                                        ? new Date(s.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                        : '—';
                                    const stageStr = s.stage_name ? ` · ${s.stage_name}` : '';
                                    const lineup = s.festival_name && s.festival_slug
                                        ? <><Link to={`/festivals/${s.festival_slug}`} style={styles.tableLink}>{s.festival_name}</Link>{s.headliners ? ` — ${s.headliners}` : ''}</>
                                        : s.headliners || s.support_acts
                                            ? <>{s.headliners && <span>{s.headliners}</span>}{s.support_acts && <span style={{ color: '#666', fontSize: '12px' }}>{s.headliners ? ' · ' : ''}{s.support_acts}</span>}</>
                                            : '—';
                                    return (
                                        <tr key={s.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                                                <Link to={`/events/${s.event_slug || s.event_id}`} style={styles.tableLink}>{formattedDate}</Link>
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                {stageStr ? stageStr.replace(' · ', '') : '—'}
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{lineup}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#555', textAlign: 'right' }}>
                        {filtered.length} show{filtered.length !== 1 ? 's' : ''} without a flyer
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

function VenueDetail({ slugOverride } = {}) {
    const params = useParams();
    const slug = slugOverride || params.slug;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [activeFlyersSubTab, setActiveFlyersSubTab] = useState(0);
    const [photosOpen, setPhotosOpen] = useState(false);
    const [photos, setPhotos] = useState([]);
    const [photosLoading, setPhotosLoading] = useState(false);
    const [photosError, setPhotosError] = useState('');
    const [photosLoaded, setPhotosLoaded] = useState(false);
    const [lightbox, setLightbox] = useState(null);
    const [defaultImageId, setDefaultImageId] = useState(null);
    const [defaultImageUrl, setDefaultImageUrl] = useState(null);
    const [defaultImageResolving, setDefaultImageResolving] = useState(false);
    const [attendedMap, setAttendedMap] = useState({});
    const [performedMap, setPerformedMap] = useState({});
    const [workedMap, setWorkedMap] = useState({});
    const [togglingMap, setTogglingMap] = useState({});

    const loadAttendedStatus = useCallback(async (shows) => {
        if (!shows || shows.length === 0) return;
        try {
            const eventIds = shows.map(s => s.event_id);
            const statusMap = await fetchBatchEventStatus(eventIds);
            const attendedMap2 = {};
            const performedMap2 = {};
            const workedMap2 = {};
            Object.entries(statusMap).forEach(([id, status]) => {
                attendedMap2[id] = status.attended;
                performedMap2[id] = status.performed || false;
                workedMap2[id] = status.worked || false;
            });
            setAttendedMap(attendedMap2);
            setPerformedMap(performedMap2);
            setWorkedMap(workedMap2);
        } catch (e) { /* not logged in */ }
    }, []);

    const handleToggleAttended = useCallback(async (eventId) => {
        setTogglingMap(prev => ({ ...prev, [eventId]: true }));
        try {
            const result = await toggleAttended(eventId);
            setAttendedMap(prev => ({ ...prev, [eventId]: result.attended }));
            setPerformedMap(prev => ({ ...prev, [eventId]: result.performed || false }));
            setWorkedMap(prev => ({ ...prev, [eventId]: result.worked || false }));
        } catch (e) {
            console.error('toggleAttended error:', e);
        } finally {
            setTogglingMap(prev => ({ ...prev, [eventId]: false }));
        }
    }, []);

    const fetchVenue = async () => {
        try {
            const res = await axios.get(`${config.API_URL}/venues/${slug}`);
            setData(res.data);
            setLoading(false);
            setDefaultImageId(null);
            setDefaultImageUrl(null);
            setDefaultImageResolving(false);
            if (res.data?.venue?.default_image_id) {
                setDefaultImageId(res.data.venue.default_image_id);
                setDefaultImageResolving(true);
                try {
                    const token = await getIdToken();
                    const imgRes = await axios.get(`${config.API_URL}/images`, {
                        params: { entityType: 'venue', entityId: res.data.venue.id },
                        headers: { Authorization: token },
                    });
                    const imgs = imgRes.data.images || [];
                    const defaultImg = imgs.find(i => i.id === res.data.venue.default_image_id);
                    if (defaultImg) setDefaultImageUrl(defaultImg.image_url);
                } catch { /* fall back to venue.image_url */ }
                finally { setDefaultImageResolving(false); }
            }
            if (res.data && res.data.venue) {
                document.title = `SHARC - ${res.data.venue.name}`;
            }
            if (res.data && res.data.show_history) {
                loadAttendedStatus(res.data.show_history);
            }
        } catch {
            setError('Failed to load venue');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVenue();
        return () => { document.title = 'SHARC - Shows Archive'; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const togglePhotos = async () => {
        const opening = !photosOpen;
        setPhotosOpen(opening);
        if (opening && !photosLoaded && data) {
            setPhotosLoading(true);
            try {
                const token = await getIdToken();
                const res = await axios.get(`${config.API_URL}/images`, {
                    params: { entityType: 'venue', entityId: data.venue.id },
                    headers: { Authorization: token },
                });
                setPhotos(res.data.images || []);
                setPhotosLoaded(true);
            } catch {
                setPhotosError('Failed to load photos.');
            } finally {
                setPhotosLoading(false);
            }
        }
    };

    const handleSetDefault = async (image) => {
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: image.id, entityType: 'venue', entityId: data.venue.id },
                { headers: { Authorization: token } }
            );
            setDefaultImageId(image.id);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to set default image.');
        }
    };

    const handleRemoveDefault = async () => {
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: null, entityType: 'venue', entityId: data.venue.id },
                { headers: { Authorization: token } }
            );
            setDefaultImageId(null);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to remove default image.');
        }
    };

    if (loading) return <div style={styles.message}>Loading venue...</div>;
    if (error) return <div style={styles.error}>{error}</div>;
    if (!data) return null;

    const { venue, stats, links, stages, show_history, artists_hosted, promoters_hosted } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'
        });
    };

    return (
        <div>
            {/* Nav row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Link to="/venues" style={styles.backLink}>← Back to Venues</Link>
                <Link to={`/venues/${venue.slug || venue.id}/edit`} style={styles.editButton}>Edit Venue</Link>
            </div>

            {/* Venue Header */}
            <div style={styles.header}>
                <div style={(defaultImageResolving ? false : (defaultImageUrl || venue.image_url)) ? styles.headerWithImage : undefined}>
                    {!defaultImageResolving && (defaultImageUrl || venue.image_url) && (
                        <img
                            src={defaultImageUrl || venue.image_url}
                            alt={venue.name}
                            style={styles.venueImage}
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    )}
                    <div style={styles.headerText}>
                        <h1 style={styles.title}>{venue.name}</h1>

                        <div style={styles.location}>
                            {[venue.street, venue.city, venue.state, venue.country].filter(Boolean).join(', ')}
                            {venue.postal_code && ` ${venue.postal_code}`}
                        </div>

                        <div style={styles.tags}>
                            {venue.venue_type && <span style={styles.tag}>{venue.venue_type}</span>}
                            {venue.age_limit && <span style={styles.tag}>{venue.age_limit}</span>}
                            <span style={{
                                ...styles.tag,
                                color: venue.status === 'Open' ? '#03dac6' : venue.status === 'Closed' ? '#cf6679' : '#ffb74d'
                            }}>
                                {venue.status}
                            </span>
                        </div>
                    </div>
                </div>

                {venue.description && (
                    <div style={styles.bio}>{venue.description}</div>
                )}
            </div>

            {/* Links */}
            {links && links.length > 0 && (
                <div style={styles.linksRow}>
                    {links.map(link => (
                        <a
                            key={link.id}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.linkChip}
                        >
                            {link.description} ↗
                        </a>
                    ))}
                </div>
            )}

            {/* Key Stats */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{venue.capacity ? venue.capacity.toLocaleString() : '—'}</div>
                    <div style={styles.statLabel}>Capacity</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.total_shows || 0}</div>
                    <div style={styles.statLabel}>Total Shows</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{artists_hosted ? artists_hosted.length : 0}</div>
                    <div style={styles.statLabel}>Artists Hosted</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>
                        {stats.first_show_date ? new Date(stats.first_show_date).getFullYear() : '—'}
                    </div>
                    <div style={styles.statLabel}>First Show</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>
                        {stats.last_show_date ? new Date(stats.last_show_date).getFullYear() : '—'}
                    </div>
                    <div style={styles.statLabel}>Last Show</div>
                </div>
            </div>

            {/* First / Last show date bar */}
            <div style={styles.historyGrid}>
                <div style={styles.historyItem}>
                    <div style={styles.historyLabel}>First Show</div>
                    <div style={styles.historyValue}>{formatDate(stats.first_show_date)}</div>
                </div>
                <div style={styles.historyItem}>
                    <div style={styles.historyLabel}>Most Recent Show</div>
                    <div style={styles.historyValue}>{formatDate(stats.last_show_date)}</div>
                </div>
            </div>

            {/* Photos — collapsible */}
            <div style={styles.collapsibleSection}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button onClick={togglePhotos} style={{ ...styles.collapsibleHeader, flex: 1 }}>
                        <span style={styles.collapsibleTitle}>📷 Photos</span>
                        <span style={styles.collapsibleChevron}>{photosOpen ? '▲' : '▼'}</span>
                    </button>
                    {defaultImageId && (
                        <button onClick={handleRemoveDefault} style={styles.removeDefaultBtn}>
                            ✕ Remove Default
                        </button>
                    )}
                </div>
                {photosOpen && (
                    <div style={styles.collapsibleBody}>
                        {photosLoading && <div style={styles.photosEmpty}>Loading photos…</div>}
                        {photosError && <div style={styles.photosEmpty}>{photosError}</div>}
                        {!photosLoading && !photosError && photos.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                <div style={{ fontSize: '28px', marginBottom: '8px' }}>📷</div>
                                <div style={{ color: '#666', fontSize: '14px', marginBottom: '12px' }}>No photos yet for this venue.</div>
                                <a href="/my-images" style={styles.uploadLink}>+ Upload a Photo</a>
                                <div style={{ color: '#555', fontSize: '12px', marginTop: '8px' }}>After uploading, assign it to this venue using ID #{venue.id}</div>
                            </div>
                        )}
                        {!photosLoading && photos.length > 0 && (
                            <div style={styles.photoGrid}>
                                {photos.map(img => {
                                    const isDefault = img.id === defaultImageId;
                                    return (
                                        <div key={img.id} style={{ ...styles.photoCard, ...(isDefault ? { borderColor: '#bb86fc' } : {}) }}>
                                            <div style={styles.photoThumb} onClick={() => setLightbox(img)}>
                                                <img src={img.image_url} alt={img.caption || ''} style={styles.photoThumbImg} loading="lazy" />
                                                {isDefault && <span style={styles.defaultBadge}>★ Default</span>}
                                                {!img.is_public && <span style={styles.privateBadge}>🔒</span>}
                                            </div>
                                            <div style={styles.photoCardBody}>
                                                {img.caption && <div style={styles.photoCaption}>{img.caption}</div>}
                                                {img.is_public && !isDefault && (
                                                    <button onClick={() => handleSetDefault(img)} style={styles.setDefaultBtn}>
                                                        ★ Set as Default
                                                    </button>
                                                )}
                                                {isDefault && <div style={{ fontSize: '11px', color: '#bb86fc', textAlign: 'center', padding: '4px 0' }}>Current default</div>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {lightbox && (
                <div style={styles.lightboxOverlay} onClick={() => setLightbox(null)}>
                    <div style={{ position: 'relative' }}>
                        <img src={lightbox.image_url} alt={lightbox.caption || ''} style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '8px' }} />
                        {lightbox.caption && <div style={styles.lightboxCaption}>{lightbox.caption}</div>}
                        <button onClick={() => setLightbox(null)} style={styles.lightboxClose}>✕</button>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div style={styles.tabsWrapper}>
                <div style={styles.tabBar}>
                    {TABS.map((tab, i) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(i)}
                            style={{
                                ...styles.tabBtn,
                                ...(activeTab === i ? styles.tabBtnActive : {}),
                            }}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
                <div style={styles.tabContent}>
                    {activeTab === 0 && <ShowHistoryTab shows={show_history} attendedMap={attendedMap} performedMap={performedMap} workedMap={workedMap} onToggleAttended={handleToggleAttended} togglingMap={togglingMap} />}
                    {activeTab === 1 && <ShowsByYearTab shows={show_history} />}
                    {activeTab === 2 && <ArtistsHostedTab artists={artists_hosted} />}
                    {activeTab === 3 && <PromotersHostedTab promoters={promoters_hosted} />}
                    {activeTab === 4 && (
                        <StagesTab
                            stages={stages}
                            venueId={venue.id}
                            onStagesUpdated={fetchVenue}
                        />
                    )}
                    {activeTab === 5 && (
                        <div>
                            <SubTabBar tabs={FLYERS_SUBTABS} active={activeFlyersSubTab} onChange={setActiveFlyersSubTab} />
                            <div style={styles.subTabContent}>
                                {activeFlyersSubTab === 0 && <FlyerHistoryTab shows={show_history} />}
                                {activeFlyersSubTab === 1 && <ShowsWithoutFlyersTab shows={show_history} />}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px' },
    editButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '6px 16px', fontSize: '14px', textDecoration: 'none', fontWeight: 'bold' },
    header: { marginBottom: '24px' },
    headerWithImage: { display: 'flex', gap: '24px', alignItems: 'flex-start', marginBottom: '16px' },
    headerText: { flex: 1 },
    venueImage: { width: '140px', height: '140px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #333', flexShrink: 0 },
    title: { fontSize: '40px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '6px' },
    location: { fontSize: '16px', color: '#aaa', marginBottom: '8px' },
    tags: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' },
    tag: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '4px 10px', fontSize: '13px', color: '#bb86fc' },
    bio: { fontSize: '15px', color: '#ccc', lineHeight: '1.7', marginTop: '16px', padding: '16px', backgroundColor: '#1e1e1e', borderRadius: '8px', border: '1px solid #2a2a2a' },
    linksRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '32px' },
    linkChip: { backgroundColor: '#1e1e1e', border: '1px solid #444', borderRadius: '4px', padding: '6px 14px', fontSize: '13px', color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px', textAlign: 'center' },
    statValue: { fontSize: '36px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },
    historyGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px' },
    historyItem: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px' },
    historyLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
    historyValue: { fontSize: '18px', fontWeight: 'bold', color: '#e0e0e0' },
    tabsWrapper: { marginTop: '8px' },
    tabBar: { display: 'flex', borderBottom: '2px solid #333', marginBottom: '0', overflowX: 'auto', gap: '2px' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', padding: '12px 20px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.3px' },
    tabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '200px' },
    subTabBar: { display: 'flex', borderBottom: '1px solid #2e2e2e', gap: '2px', marginBottom: '0', overflowX: 'auto' },
    subTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-1px', padding: '8px 16px', fontSize: '13px', fontWeight: '500', color: '#666', cursor: 'pointer', whiteSpace: 'nowrap' },
    subTabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    subTabContent: { paddingTop: '20px' },
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '11px 16px', color: '#e0e0e0', borderBottom: '1px solid #222' },
    trEven: { backgroundColor: '#1e1e1e' },
    trOdd: { backgroundColor: '#1a1a1a' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    attendedBtnOff: { backgroundColor: 'transparent', color: '#666', border: '1px solid #444', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
    attendedBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' },
    performedBadge: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    workedBadge: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    showCount: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    pagination: { display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '20px', flexWrap: 'wrap' },
    pageBtn: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 12px', fontSize: '13px', color: '#aaa', cursor: 'pointer' },
    pageBtnActive: { backgroundColor: '#bb86fc', borderColor: '#bb86fc', color: '#121212', fontWeight: 'bold' },
    pageBtnDisabled: { opacity: 0.35, cursor: 'default' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    emptyTab: { textAlign: 'center', color: '#666', padding: '60px 0', fontSize: '15px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },
    // Collapsible photos
    collapsibleSection: { marginBottom: '32px' },
    collapsibleHeader: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '14px 20px', cursor: 'pointer', color: '#e0e0e0' },
    collapsibleTitle: { fontSize: '16px', fontWeight: '600' },
    collapsibleChevron: { fontSize: '12px', color: '#888' },
    collapsibleBody: { backgroundColor: '#1a1a1a', border: '1px solid #333', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '20px' },
    photosEmpty: { textAlign: 'center', color: '#666', padding: '20px 0', fontSize: '14px' },
    uploadLink: { backgroundColor: '#bb86fc', color: '#121212', borderRadius: '6px', padding: '7px 16px', fontSize: '13px', fontWeight: '600', textDecoration: 'none' },
    photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' },
    photoCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', overflow: 'hidden' },
    photoThumb: { position: 'relative', width: '100%', paddingTop: '75%', overflow: 'hidden', cursor: 'zoom-in', background: '#111' },
    photoThumbImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
    privateBadge: { position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px 4px', fontSize: '12px' },
    photoCardBody: { padding: '8px' },
    photoCaption: { fontSize: '11px', color: '#aaa', marginBottom: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    setDefaultBtn: { width: '100%', background: 'transparent', border: '1px solid #bb86fc', borderRadius: '4px', color: '#bb86fc', fontSize: '11px', padding: '4px 6px', cursor: 'pointer' },
    removeDefaultBtn: { background: 'transparent', border: '1px solid #555', borderRadius: '6px', color: '#aaa', padding: '8px 12px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
    defaultBadge: { position: 'absolute', top: '6px', left: '6px', background: '#bb86fc', color: '#121212', fontSize: '10px', fontWeight: 'bold', borderRadius: '4px', padding: '2px 6px' },
    lightboxOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 },
    lightboxCaption: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: '#e0e0e0', padding: '8px 12px', fontSize: '13px', textAlign: 'center', borderRadius: '0 0 8px 8px' },
    lightboxClose: { position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', borderRadius: '4px', padding: '2px 8px' },
    // Stages tab
    stageList: { display: 'flex', flexDirection: 'column', gap: '12px' },
    stageCard: { backgroundColor: '#222', border: '1px solid #333', borderRadius: '8px', padding: '16px' },
    stageCardInner: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' },
    stageCardLeft: { flex: 1 },
    stageName: { fontSize: '16px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '10px' },
    stageBadge: { backgroundColor: '#2c2c2c', border: '1px solid #bb86fc', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', color: '#bb86fc', fontWeight: 'bold', letterSpacing: '0.5px' },
    stageMeta: { fontSize: '13px', color: '#aaa', marginBottom: '4px' },
    stageDesc: { fontSize: '13px', color: '#888', marginTop: '6px' },
    stageCardActions: { display: 'flex', gap: '8px', flexShrink: 0 },
    stageEditBtn: { backgroundColor: '#2c2c2c', border: '1px solid #555', borderRadius: '4px', padding: '5px 12px', fontSize: '13px', color: '#ccc', cursor: 'pointer' },
    stageDeleteBtn: { backgroundColor: 'transparent', border: '1px solid #555', borderRadius: '4px', padding: '5px 12px', fontSize: '13px', color: '#cf6679', cursor: 'pointer' },
    stageFormTitle: { fontSize: '14px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    stageFormRow: { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px' },
    stageFormLabel: { fontSize: '13px', color: '#aaa', fontWeight: '600' },
    stageInput: { backgroundColor: '#1a1a1a', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    stageFormActions: { display: 'flex', gap: '10px', marginTop: '6px' },
    stageSaveBtn: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '8px 20px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
    stageCancelBtn: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #444', borderRadius: '4px', padding: '8px 16px', fontSize: '14px', cursor: 'pointer' },
    addStageBtn: { marginTop: '16px', backgroundColor: '#2c2c2c', border: '1px solid #555', borderRadius: '6px', padding: '9px 18px', fontSize: '14px', color: '#bb86fc', cursor: 'pointer', fontWeight: 'bold' },
    stageError: { backgroundColor: '#2a1a1a', border: '1px solid #cf6679', borderRadius: '6px', padding: '10px 14px', color: '#cf6679', fontSize: '13px', marginBottom: '16px' },
    // Flyer grid
    flyerControlsBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' },
    flyerFilterGroup: { display: 'flex', alignItems: 'center', gap: '6px', flex: '0 1 auto' },
    flyerFilterTypeSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', color: '#e0e0e0', cursor: 'pointer', flexShrink: 0 },
    flyerFilterInput: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', color: '#e0e0e0', width: '180px', outline: 'none' },
    flyerFilterClear: { background: 'transparent', border: 'none', color: '#666', fontSize: '14px', cursor: 'pointer', padding: '4px 6px', lineHeight: 1 },
    flyerSortGroup: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
    flyerSortLabel: { fontSize: '13px', color: '#888', whiteSpace: 'nowrap' },
    flyerSortSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', color: '#e0e0e0', cursor: 'pointer' },
    flyerGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '8px' },
    flyerCell: { position: 'relative', display: 'block', aspectRatio: '8.5 / 11', overflow: 'hidden', borderRadius: '4px', border: '1px solid #2e2e2e', backgroundColor: '#111', textDecoration: 'none', cursor: 'pointer' },
    flyerCellPad: { aspectRatio: '8.5 / 11', visibility: 'hidden' },
    flyerImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block', transition: 'opacity 0.15s ease' },
    flyerOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)', padding: '20px 6px 5px', transition: 'opacity 0.15s ease' },
    flyerDate: { fontSize: '10px', color: '#e0e0e0', fontWeight: '600', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    flyerErrorBox: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: '#161616', border: '1px dashed #2e2e2e', borderRadius: '4px', padding: '12px' },
    flyerErrorIcon: { fontSize: '28px', opacity: 0.25 },
    flyerErrorDate: { fontSize: '10px', color: '#555', fontWeight: '600', textAlign: 'center', lineHeight: 1.4 },
    flyerErrorVenue: { fontSize: '10px', color: '#444', textAlign: 'center', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
};

export default VenueDetail;
